import { useCallback, useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, X } from "lucide-react";

const MAX_IMAGES = 10;
const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];

function isImage(f) {
  return f.type.startsWith("image/");
}

export const Uploader = ({ files, setFiles, disabled }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [warning, setWarning] = useState("");

  const ingest = useCallback(
    (incoming) => {
      setWarning("");
      const fresh = Array.from(incoming).filter((f) =>
        ACCEPTED.includes(f.type)
      );
      if (fresh.length === 0) {
        setWarning("Only JPG, PNG, WEBP or PDF are accepted.");
        return;
      }

      const hasPdfInIncoming = fresh.some((f) => f.type === "application/pdf");
      const hasPdfAlready = files.some((f) => f.type === "application/pdf");
      const imagesIncoming = fresh.filter(isImage);

      // Rule: either a single PDF OR up to MAX_IMAGES images.
      if (hasPdfInIncoming) {
        const pdf = fresh.find((f) => f.type === "application/pdf");
        setFiles([pdf]);
        return;
      }
      if (hasPdfAlready) {
        setFiles(imagesIncoming.slice(0, MAX_IMAGES));
        return;
      }
      const merged = [...files, ...imagesIncoming];
      if (merged.length > MAX_IMAGES) {
        setWarning(`Capped at ${MAX_IMAGES} images.`);
      }
      setFiles(merged.slice(0, MAX_IMAGES));
    },
    [files, setFiles]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      ingest(e.dataTransfer.files);
    },
    [ingest, disabled]
  );

  const onPick = useCallback(
    (e) => {
      ingest(e.target.files || []);
      e.target.value = "";
    },
    [ingest]
  );

  const removeAt = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    setFiles(next);
  };

  const isEmpty = files.length === 0;
  const onlyPdf =
    files.length === 1 && files[0].type === "application/pdf" ? files[0] : null;

  return (
    <div data-testid="uploader">
      <label
        htmlFor="hk-file-input"
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className="block cursor-pointer transition"
        style={{
          border: `1.5px dashed ${
            dragOver ? "#00BFA5" : "rgba(232,240,247,0.18)"
          }`,
          background: dragOver
            ? "rgba(0,191,165,0.06)"
            : "rgba(13,35,64,0.35)",
          borderRadius: 14,
          padding: "32px 24px",
        }}
        data-testid="uploader-dropzone"
      >
        <input
          ref={inputRef}
          id="hk-file-input"
          type="file"
          multiple
          accept={ACCEPTED.join(",")}
          onChange={onPick}
          className="hidden"
          disabled={disabled}
          data-testid="uploader-file-input"
        />
        {isEmpty ? (
          <div className="flex flex-col items-center text-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(0,191,165,0.10)",
                border: "1px solid rgba(0,191,165,0.35)",
              }}
            >
              <Upload size={20} color="#00BFA5" />
            </div>
            <div>
              <div
                className="font-display"
                style={{ fontSize: 22, fontWeight: 700, color: "#E8F0F7" }}
              >
                Drop your study material
              </div>
              <div style={{ color: "#7A9BB5", fontSize: 14, marginTop: 4 }}>
                Up to 10 images (JPG · PNG · WEBP) — or a single PDF
              </div>
            </div>
            <button
              type="button"
              className="hk-btn-ghost px-4 py-1.5 text-sm mt-1"
              onClick={(e) => {
                e.preventDefault();
                inputRef.current?.click();
              }}
              data-testid="uploader-browse-btn"
            >
              Browse files
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span
                className="font-mono"
                style={{ color: "#7A9BB5", fontSize: 12, letterSpacing: "0.08em" }}
              >
                {onlyPdf
                  ? "PDF ATTACHED"
                  : `${files.length} / ${MAX_IMAGES} IMAGES`}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  inputRef.current?.click();
                }}
                className="hk-btn-ghost px-3 py-1 text-xs"
                data-testid="uploader-add-more-btn"
              >
                + Add more
              </button>
            </div>

            {onlyPdf ? (
              <div
                className="flex items-center gap-3 rounded-lg px-4 py-3"
                style={{
                  background: "rgba(0,191,165,0.06)",
                  border: "1px solid rgba(0,191,165,0.25)",
                }}
                data-testid="uploader-pdf-tile"
              >
                <FileText size={20} color="#00BFA5" />
                <div className="flex-1 min-w-0">
                  <div className="truncate" style={{ color: "#E8F0F7" }}>
                    {onlyPdf.name}
                  </div>
                  <div style={{ color: "#7A9BB5", fontSize: 12 }}>
                    {(onlyPdf.size / 1024).toFixed(0)} KB · application/pdf
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setFiles([]);
                  }}
                  className="hk-btn-ghost p-1"
                  aria-label="remove pdf"
                  data-testid="uploader-remove-pdf-btn"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="group relative rounded-lg overflow-hidden"
                    style={{
                      background: "rgba(13,35,64,0.6)",
                      border: "1px solid rgba(232,240,247,0.08)",
                      aspectRatio: "3 / 4",
                    }}
                    data-testid={`uploader-thumb-${i}`}
                  >
                    <img
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        removeAt(i);
                      }}
                      className="absolute top-1 right-1 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      style={{
                        background: "rgba(6,15,26,0.85)",
                        color: "#E8F0F7",
                      }}
                      aria-label={`remove ${f.name}`}
                      data-testid={`uploader-thumb-remove-${i}`}
                    >
                      <X size={12} />
                    </button>
                    <div
                      className="absolute bottom-0 left-0 right-0 px-2 py-1 truncate"
                      style={{
                        background:
                          "linear-gradient(180deg, transparent, rgba(6,15,26,0.85))",
                        fontSize: 11,
                        color: "#E8F0F7",
                      }}
                    >
                      <ImageIcon
                        size={10}
                        className="inline mr-1 opacity-70"
                      />
                      {f.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </label>

      {warning && (
        <div
          className="mt-2 text-sm"
          style={{ color: "#F5A623" }}
          data-testid="uploader-warning"
        >
          {warning}
        </div>
      )}
    </div>
  );
};

export default Uploader;
